import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

interface CSVFileUploaderProps {
  uploadedFile: File | null
  onFileChange: (file: File | null) => void
  label?: string
  disabled?: boolean
}

export function CSVFileUploader({
  uploadedFile,
  onFileChange,
  label = 'CSV File',
  disabled = false,
}: CSVFileUploaderProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileChange(acceptedFiles[0])
      }
    },
    [onFileChange],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    disabled,
  })

  return (
    <div>
      <Label>{label}</Label>
      <div
        {...getRootProps()}
        className={cn(
          'mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
          disabled
            ? 'border-border bg-muted cursor-not-allowed'
            : isDragActive
              ? 'border-info bg-info-subtle'
              : 'border-border hover:border-muted-foreground/40 bg-muted hover:bg-muted/80',
        )}
      >
        <input {...getInputProps()} />
        <Upload className="h-5 w-5 text-muted-foreground mb-2" />
        {uploadedFile ? (
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">
              {uploadedFile.name}
            </p>
            {!disabled && (
              <p className="text-xs text-muted-foreground mt-1">
                Click or drag to replace
              </p>
            )}
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {disabled
                ? 'File upload disabled'
                : isDragActive
                  ? 'Drop the file here'
                  : 'Drag & drop a CSV file here'}
            </p>
            {!disabled && (
              <p className="text-xs text-muted-foreground mt-1">
                or click to select file
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
